/**
 * Attribution capture and money/rate formatting for the affiliate program.
 *
 * NO API CALLS LIVE HERE ANY MORE. Both affiliate surfaces are on `/v1`: the
 * creator's own standing is `GET /v1/me/affiliate` (`fetchMyAffiliate`,
 * `@api/v1/me`, group A — it is account-scoped, so it belongs with `/v1/me`), and
 * the superadmin roster is group K in `@api/v1/admin`. What is left is the half of
 * this module that was never HTTP: the last-touch `?via` capture the landing page and
 * the register page share, and the two formatters that are the single place integer
 * cents and basis points become "$52.20" / "30%".
 */

// --- attribution capture (last-touch ?via, stored in localStorage) -----------

const VIA_KEY = 'sideguide_via';
const VIA_MAX_AGE_DAYS = 60;

/**
 * Landing-page hook: if the URL carries `?via=CODE`, store it as `{code, ts}`
 * (last-touch — a newer code always overwrites). An empty/missing code never
 * clobbers a stored one. Silent on any failure (private mode, malformed URL).
 */
export function captureVia(): void {
  try {
    const code = new URLSearchParams(window.location.search).get('via')?.trim();
    if (!code) return;
    localStorage.setItem(VIA_KEY, JSON.stringify({ code, ts: Date.now() }));
  } catch {
    /* ignore — best effort */
  }
}

/**
 * Register-time read: the stored referral code if it's still within the 60-day
 * window, else null. A stale/absent/malformed code resolves to null so a signup
 * never carries dead attribution.
 */
export function storedVia(): string | null {
  try {
    const raw = localStorage.getItem(VIA_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { code?: unknown; ts?: unknown };
    const code = typeof parsed.code === 'string' ? parsed.code.trim() : '';
    const ts = typeof parsed.ts === 'number' ? parsed.ts : 0;
    if (!code || !ts) return null;
    if ((Date.now() - ts) / 86400000 > VIA_MAX_AGE_DAYS) return null;
    return code;
  } catch {
    return null;
  }
}

/**
 * The link an affiliate shares — the inverse of `captureVia`, and deliberately next
 * to it.
 *
 * `?via=` is a wire format shared by three places: the landing page reads it, the
 * register page redeems what it stored, and this writes it. It was built inline in
 * `AdminAffiliate` and nowhere else, so the superadmin console had no way to hand an
 * affiliate their link without an operator knowing the convention and typing the URL
 * by hand. One definition; change the parameter here and every surface follows.
 *
 * `origin` is injectable ONLY so a test can pin it — in the browser the default is
 * always right, and hard-coding a production host would break every preview deploy.
 */
export function referralLink(code: string, origin = location.origin): string {
  return code ? `${origin}/?via=${encodeURIComponent(code)}` : '';
}

// --- formatting --------------------------------------------------------------

/** Integer cents → "$52.20" (always two decimals; negatives as "-$5.00"). */
export function fmtUsd(cents: number): string {
  const neg = cents < 0;
  const v = Math.abs(cents) / 100;
  return `${neg ? '-' : ''}$${v.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Basis points → percent, e.g. 3000 → "30%", 2550 → "25.5%". */
export function fmtRate(bps: number): string {
  const pct = bps / 100;
  return `${Number.isInteger(pct) ? pct : pct.toFixed(1)}%`;
}

// --- where the calls went ------------------------------------------------------
//
// `AffiliateAdminRow` and its four superadmin calls (`fetchAffiliates`,
// `createAffiliate`, `updateAffiliate`, `markAffiliatePaid`) moved to `/v1` —
// `@api/v1/admin`, group K. Three things changed with them: the three balance
// buckets are nested under `totals` (one `AffiliateTotals` shared by four routes),
// `mark-paid` became `POST /v1/affiliates/{user_id}/payouts` because a payout is a
// thing that is created, and that response carries the UPDATED console row — the last
// of doc 02 W9's write-then-reload chains on that page.
//
// `fetchAffiliateSummary` and its five types were the last legacy read on the
// creator-facing page and are now `fetchMyAffiliate` / `MyAffiliate`
// (`@api/v1/me`). The route had shipped without `referrals[]`, `payouts[]` and
// `paying_community_count` — the assembly lived inside `api/affiliates.py`, a router
// that a v1 route may neither import from nor copy — and `api/v1/principal.py` now
// calls `domain.affiliates.creator_summary`, which is where it lives. Migrating fixed
// a live 500 as well: the legacy handler rebinds the imported `holder_flags` FUNCTION
// to a dict and then calls it (`api/affiliates.py:151,159`), so it raises `TypeError`
// for any affiliate with at least one referral.
